import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import PriceTable from "@/components/PriceTable";
import CTASection from "@/components/CTASection";
import ZWImage from "@/components/ZWImage";
import { HAUSMEISTER_ROWS, MONATSVERTRAEGE, HINWEISE, CONTACT } from "@/data/services";

export default function Hausmeisterservice() {
  return (
    <>
      <SEO
        title="Hausmeisterservice Herten – Objektbetreuung & Monatsverträge | ZWILLINGSWERK"
        description="Hausmeisterservice in Herten + 20 km: Objektkontrolle 45 €/Std., Treppenhausreinigung 3 €/m², Monatsverträge ab 199 €. Für Verwaltungen, Eigentümer & Gewerbe."
      />
      <PageHero
        eyebrow="Hausmeisterservice · Verwaltungen & Eigentümer"
        title="Zuverlässig. Flexibel. Rundum betreut."
        description="Wir übernehmen die regelmäßige Objektbetreuung und sorgen dafür, dass alles in und um Ihr Gebäude stets in einem einwandfreien Zustand ist. Für private, gewerbliche und Immobilien."
        testid="hausmeister-hero"
      />

      <section className="py-14 bg-[#F8FAF8] border-b border-[#D7E5D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <PriceTable
                title="Hausmeister & Objektbetreuung"
                subtitle="Laufende Pflege für Verwaltungen & Eigentümer"
                badge="Laufend / Einzeln"
                rows={HAUSMEISTER_ROWS}
                footnote="* Z. B. Glühbirnen wechseln, Türbeschläge nachstellen, kleine Ausbesserungen – soweit handwerklich zulässig."
                testid="price-table-hausmeister"
              />
            </div>
            <aside className="lg:col-span-4 space-y-6">
              <ZWImage
                src="/images/hausmeister.png"
                fallback="https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Hausmeister bei der Treppenhausreinigung in einem Mehrfamilienhaus in Herten"
                className="w-full aspect-[4/3] rounded-2xl shadow-lg"
                subcaption="Objektbetreuung"
                caption="Sauberes Treppenhaus, gepflegte Anlage"
                testid="hausmeister-image"
              />
              <div className="bg-white border border-[#D7E5D9] rounded-2xl p-6 space-y-3 text-xs text-[#4A634E]">
                <h2 className="text-sm font-bold text-[#122B18] uppercase tracking-wider">Wichtige Hinweise</h2>
                <p>{HINWEISE.mindest}</p>
                <p>{HINWEISE.anfahrt}</p>
                <p>{HINWEISE.zahlung}</p>
                <p>
                  Bestimmte Tätigkeiten im Hausmeisterservice sind handwerksrechtlich begrenzt – wir sagen Ihnen
                  offen, was wir selbst machen dürfen und wofür ein Fachbetrieb nötig ist.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Monatsverträge */}
      <section className="py-16 bg-white border-b border-[#D7E5D9]" data-testid="monatsvertraege-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
            <span className="text-xs font-bold text-[#1E5631] uppercase tracking-wider">
              Objektbetreuung – Monatsverträge
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-[#122B18] font-display">
              Basic, Standard oder Komplett
            </h2>
            <p className="text-[#4A634E] text-base">
              Feste monatliche Betreuung ohne Personalaufwand. Alle Preise zzgl. 19 % MwSt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {MONATSVERTRAEGE.map((p) => (
              <div
                key={p.name}
                data-testid={`hm-package-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-2xl p-6 flex flex-col justify-between ${
                  p.highlight
                    ? "border-2 border-[#1E5631] bg-white shadow-xl relative"
                    : "border-2 border-[#D7E5D9] bg-[#F8FAF8]"
                }`}
              >
                {p.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#1E5631] text-white text-xs font-black uppercase tracking-wider px-4 py-1 rounded-full whitespace-nowrap">
                    Beliebteste Wahl
                  </div>
                )}
                <div className="space-y-4">
                  <div className="flex justify-between items-center gap-2 pt-1">
                    <h3 className="text-xl font-black text-[#122B18] font-display">{p.name}</h3>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded whitespace-nowrap ${
                        p.highlight ? "bg-[#1E5631] text-white" : "bg-[#E6F3E6] text-[#1E5631]"
                      }`}
                    >
                      {p.tag}
                    </span>
                  </div>
                  <div className="py-2">
                    <span className="text-4xl font-black text-[#1E5631] font-display">{p.price}</span>
                    <span className="text-xs text-[#5D7761]"> / Monat zzgl. MwSt.</span>
                  </div>
                  <ul className="space-y-2.5 text-xs sm:text-sm text-[#3A523E]">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <span className="text-[#1E5631] font-black">✓</span> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <a
                  href={CONTACT.phoneHref}
                  data-testid={`hm-${p.name.toLowerCase().replace(/\s+/g, "-")}-cta`}
                  className={`mt-6 block text-center font-bold py-3 rounded-lg text-sm transition-colors ${
                    p.highlight
                      ? "bg-[#1E5631] hover:bg-[#154023] text-white shadow-md"
                      : "bg-white hover:bg-[#F2F7F2] text-[#1E5631] border border-[#1E5631]"
                  }`}
                >
                  {p.name.replace("Paket ", "")} anfragen
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-[#5D7761] mt-6">
            Auch interessant:{" "}
            <Link to="/winterdienst" className="text-[#1E5631] font-bold underline" data-testid="hm-winter-link">
              Winterdienst-Saisonverträge ab 350 €
            </Link>
          </p>
        </div>
      </section>

      <CTASection />
    </>
  );
}
